import AutoComplete from './autocomplete';
import dom from '../util/dom';
import distance from '../util/distance';
import cookie from '../util/cookie';

// places-api constants
const placesbase = 'https://places.cloud.starry.com/autocomplete';

// signup constants
const signupUrl = window.signup_url;

// place props
const placeProps = [
  'place_id',
  'lat',
  'lng',
  'state',
  'city',
  'neighborhood',
  'borough',
  'postal_code',
  'street_number',
  'street',
  'country'
];

const placeholders = [
  { lat: 42.3600, lng: -71.0588, placeholder: '123 Main St, Boston MA' },
  { lat: 40.7127, lng: -74.0059, placeholder: '123 Main St, New York NY' },
  { lat: 34.0522, lng: -118.2436, placeholder: '123 Main St, Los Angeles CA' },
  { lat: 39.7392, lng: -104.9902, placeholder: '123 Main St, Denver CO' },
  { lat: 38.8920, lng: -77.0199, placeholder: '123 Main St, Washington DC' },
  { lat: 39.9612, lng: -82.9988, placeholder: '123 Main St, Columbus OH' },
  { lat: 37.7749, lng: -122.4194, placeholder: '123 Main St, San Francisco CA' }
];

const url_params = location.search.substr( 1 ).split('&').reduce( ( a, c ) => {
  const pair = c.split('=');
  a[ pair[ 0 ] ] = pair[ 1 ];
  return a;
}, {} );

export default class PlacesAutoComplete extends AutoComplete {

  constructor( opts ) {
    super( opts );

    this.input = opts.input;
    // set default focal point for geolocation searches
    this.geoip = { latitude: 42.3601, longitude: -71.0589 };
    // current search term
    this.term = '';

    this.isRateLimited = false;
    this.searchPromise = null;
    this.searchStr = '';

    // try to grab geoIP data from the cookie we set on the server
    try {
      const base64 = cookie.getItem('geoip');
      const json   = window.atob( base64 );
      const geo    = JSON.parse( json );

      if ( !geo.latitude || !geo.longitude ) {
        throw new Error;
      }

      Object.assign( this.geoip, geo );
    } catch ( err ) {}

    this.location = { lat: this.geoip.latitude, lng: this.geoip.longitude };

    const sorted = placeholders.slice().sort( ( a, b ) => {
      const da = this._getDistance( a, this.location );
      const db = this._getDistance( b, this.location );
      return da - db;
    });

    const placeholder = sorted[ 0 ].placeholder;

    this.input.placeholder = placeholder;

    this.setupRecaptcha();
  }

  // calculate the distance between 2 places (in meters)
  _getDistance( place1, place2 ) {
    return distance( place1, place2 );
  }

  // handle address with _id selected
  _onPlaceSelected( place ) {
    this._trackSelection( place, () => {
      let url = `${ signupUrl }/signup/${ place._id }`;

      if ( url_params.trial_code ) {
        url += `?trial_code=${ url_params.trial_code }`;
      }

      window.location = url;
    });
  }

  // handle address without _id selected
  _onMapboxSelected( place ) {
    this._trackSelection( place, () => {
      const copy = Object.assign( {}, place );

      delete copy.suggestion;

      const json    = JSON.stringify( copy );
      const escaped = escape( json );
      const base64  = window.btoa( escaped );
      const enc     = window.encodeURIComponent( base64 );

      let url = `${ signupUrl }/register/${ enc }`;

      if ( url_params.trial_code ) {
        url += `?trial_code=${ url_params.trial_code }`;
      }

      window.location = url;
    });
  }

  // handle "Address not found" selection
  _onNoneSelected() {
    this._trackSelection( null, () => {
      window.location = `${ signupUrl }/register`;
    });
  }

  // track a place selection
  _trackSelection( place, cb ) {
    const tracking = { search: this.term || null };

    this.onLoading();

    // initialize all required place props to null
    placeProps.forEach( key => tracking[ key ] = null );

    // if we've got a place, set the props
    if ( place ) {
      placeProps.forEach( key => tracking[ key ] = ( place[ key ] || null ) );
      tracking.place_id = place._id || null;
    }

    if ( window.acumen ) {
      const meta = { search: this.term, place_id: null, address: null };

      if ( place ) {
        const name  = place.name;
        const city  = place.city;
        const state = place.state;
        const zip   = place.postal_code;

        if ( name && city && state && zip ) {
          meta.address = `${ name }, ${ city }, ${ state }, ${ zip }`;
        }

        if ( place._id ) {
          meta.place_id = place._id;
        }
      }

      window.acumen.track( 'web-submit-check_availability', meta );

      if ( window.posthog ) {
        window.posthog.capture( 'starry_com:submit_check_availability', meta );
      }
    }

    const url     = `${ signupUrl }/availability-checks`;
    const captcha = this.recaptchaTrack();

    captcha.then( () => {
      const opts = { method: 'POST', withCredentials: true, url };
      dom.ajax( opts, tracking, ( err, res ) => {
        this.onLoaded();
        cb( err, res );
      });
    });
  }

  // set up the recaptcha script
  setupRecaptcha() {
    // no site_key? bail
    if ( !window.recaptcha_site_key ) {
      return;
    }

    // recaptcha already loaded somehow? just bind the ready callback
    if ( window.grecaptcha ) {
      return window.grecaptcha.ready( () => this.recaptchaReady = true );
    }

    const script = document.createElement('script');
    const base   = 'https://www.google.com/recaptcha/api.js';
    const query  = `render=${ window.recaptcha_site_key }`;

    script.src = `${ base }?${ query }`;
    script.type = 'text/javascript';
    script.async = true;

    script.onload = () => {
      window.grecaptcha.ready( () => this.recaptchaReady = true );
    };

    document.body.appendChild( script );
  }

  // track recaptcha response
  recaptchaTrack() {
    if ( !this.recaptchaReady ) {
      return Promise.resolve();
    }

    if ( this.recaptchaPromise ) {
      return this.recaptchaPromise;
    }

    return this.recaptchaPromise = new Promise( ( resolve, reject ) => {
      window.grecaptcha
      .execute( window.recaptcha_site_key, { action: 'check_availability' } )
      .then( token => {
        const xhr = new XMLHttpRequest();
        xhr.open( 'POST', '/recaptcha-verify' );
        xhr.setRequestHeader( 'Content-Type', 'application/json' );
        xhr.addEventListener( 'error', reject );
        xhr.addEventListener( 'load', () => {
          const json = JSON.parse( xhr.responseText );
          window.acumen.track( 'web-recaptcha-score', json );
          resolve( json );
        });
        xhr.send( JSON.stringify({ token }) );
      }, reject );
    });
  }

  // run a search, gracefully handling rate limiting
  search( str ) {
    if ( this.isRateLimited ) {
      // Set the searchStr so the initial promise searches for the new
      // search str when it is no longer rate limited
      this.searchStr = str;

      // Nasty hack to avoid showing the "Don't see yours?" thing
      // more than once. This ensures that suggest() will only get
      // called once by the initial search if we are late limited
      // Ideally, this would return this.searchPromise
      return Promise.reject('Rate limited.');
    }

    return this._searchUntilComplete( str );
  }

  // run a search, but retry until completed on rate limiting
  _searchUntilComplete( str ) {
    return new Promise( ( resolve, reject ) => {

      // Run the search
      this._searchPlacesAutocomplete( str ).then( data => {
        this.isRateLimited = false;
        resolve( data );
      }).catch( err => {
        // Remove rate limiting if not 429
        if ( !err.xhr || err.xhr.status !== 429 ) {
          this.isRateLimited = false;
          return reject( err );
        }

        // Calculate rate limiting ms
        const retryAfterStr = err.xhr.getResponseHeader('Retry-After');
        const retryAfter = parseInt( retryAfterStr, 10 );
        if ( Number.isNaN( retryAfter ) ) {
          return reject( new Error('Rate limited with bad Retry-After') );
        }

        const ms = retryAfter * 1000;

        // Create a new promise to try the search again after the timeout
        this.isRateLimited = true;
        this.searchStr = str;
        this.searchPromise = new Promise( ( resolve, reject ) => {
          setTimeout( () => {
            this._searchUntilComplete( this.searchStr )
            .then( resolve, reject );
          }, ms );
        });

        // "recurse" on the retry
        this.searchPromise.then( resolve, reject );
      });
    });
  }

  // search places-api directly, ignoring rate limiting
  _searchPlacesAutocomplete( str ) {
    let url = `${ placesbase }?search=${ str }`;

    url += `&lat=${ this.geoip.latitude }`;
    url += `&lon=${ this.geoip.longitude }`;

    this.recaptchaTrack();

    return new Promise( ( resolve, reject ) => {
      dom.ajax( { method: 'GET', url }, ( err, data ) => {
        if ( err ) {
          return reject( err );
        }

        resolve( data.slice( 0, 3 ) );
      });
    });
  }

  // override `suggest` to determine whether we need to render
  // neighborhoods, and to store the search term
  suggest( suggestions, term ) {
    this.term = term;

    // nasty hack for rendering the "Don't see yours?" thing
    suggestions.push({ notfound: true });

    return super.suggest( suggestions );
  }

  // render a single suggestion
  renderSuggestion( place ) {
    const li = document.createElement('li');

    let html = '';

    if ( place.notfound ) {
      html += 'Don\'t see yours?&nbsp;';
      html += '<span class="underline">Add your address to the list.</span>';

      li.classList.add('notfound');
      return html;
    }

    const suggestion = place.suggestion;

    html += `<strong>${ suggestion.name }</strong>`;
    html += `${ suggestion.locality }, `;
    html += `${ suggestion.state } ${ suggestion.postal_code }`;

    if ( place._id ) {
      li.classList.add('starry-place');
    }

    li.innerHTML = html;

    return li;
  }

  // handle *any* selection
  onSelect( place ) {
    if ( place && place._id ) {
      this._onPlaceSelected( place );
    } else if ( place && !place.notfound ) {
      this._onMapboxSelected( place );
    } else {
      this._onNoneSelected();
    }
  }

}
